/**
 * Google Consent Mode v2 Integration
 *
 * Bridge between ConsentModal and Google Consent Mode v2.
 *
 * Mapping:
 *   analytics  → analytics_storage
 *   advertising → ad_storage, ad_user_data, ad_personalization
 */

import type { Preferences } from '../../components/consent-modal/types';
import { loadPreferences } from '../../components/consent-modal/preferences';
import { log, warn } from './logger';

/**
 * Restore saved consent preferences to dataLayer before GTM loads.
 *
 * Consent Mode v2 defaults (all denied) are already set by an inline
 * script in the HTML template — that runs before any JS bundle parses.
 * This function only handles returning users: if saved preferences exist,
 * push a consent update so GTM picks up the granted state on first scan.
 */
export function restoreSavedConsent(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const saved = loadPreferences();
  if (!saved) {
    return;
  }

  const analyticsConsent = saved.analytics ? 'granted' : 'denied';
  const adConsent = saved.advertising ? 'granted' : 'denied';

  const consentUpdate = {
    analytics_storage: analyticsConsent,
    ad_storage: adConsent,
    ad_user_data: adConsent,
    ad_personalization: adConsent
  };

  // Use gtag() if available (defined by inline script in HTML template).
  // gtag pushes an Arguments object which GTM recognizes as a consent command.
  // Fallback to raw dataLayer.push for safety.
  if (window.gtag) {
    window.gtag('consent', 'update', consentUpdate);
  }
  else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(['consent', 'update', consentUpdate] as any);
  }

  log('[Consent] Restored saved preferences:', saved);
}

/**
 * Update Google Consent Mode based on user preferences
 *
 * Maps Tablz cookie preferences to Google Consent Mode v2 states:
 *  - `analytics`   → analytics_storage
 *  - `advertising`  → ad_storage, ad_user_data, ad_personalization
 */
export function updateConsentMode(preferences: Preferences): void {
  if (typeof window === 'undefined' || !window.gtag) {
    warn('[Consent] gtag not available — skipping consent mode update');
    return;
  }

  try {
    const analyticsConsent = preferences.analytics ? 'granted' : 'denied';
    const adConsent = preferences.advertising ? 'granted' : 'denied';

    const consentUpdate = {
      analytics_storage: analyticsConsent,
      ad_storage: adConsent,
      ad_user_data: adConsent,
      ad_personalization: adConsent
    };

    window.gtag('consent', 'update', consentUpdate);

    log('[Consent] GTM Consent Mode v2 updated:', consentUpdate);
  }
  catch (error) {
    warn('[Consent] Failed to update consent mode:', error);
  }
}
